'use client'
import { useState, forwardRef, useRef } from 'react'
import { cn, getLocalizedPath } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { Picture, Link, Text } from '../../components/index.js'
import Title from '../Title/index.js'
import SwiperBox from '../SwiperBox/index.js'
import { Container } from '../../components/container.js'
import { VideoModal } from '../VideoModal/index.js'
import { convertLexicalToHTML } from '@payloadcms/richtext-lexical/html'
import type { MediaPlayerMultiProps, MediaPlayerItemProps } from './types.js'
import { useExposure } from '../../hooks/useExposure.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'

const componentType = 'video'
const componentName = 'media_player_multi'

const MediaPlayerItem = ({ data, configuration }: { data: MediaPlayerItemProps; configuration?: any }) => {
  const ref = useRef<HTMLDivElement>(null)

  useExposure(ref, {
    componentType,
    componentName,
    componentTitle: data?.title,
    position: configuration?.index + 1,
    componentDescription: data?.description,
  })

  return (
    <div
      ref={ref}
      className={cn(
        'laptop:max-w-full w-full overflow-hidden',
        'laptop:flex-row flex shrink-0 flex-col',
        'lg-desktop:h-[480px] desktop:h-[384px] laptop:h-[360px] h-auto',
        configuration.shape === 'round' ? 'rounded-2xl' : ''
      )}
    >
      <div className="laptop:w-[49%] laptop:h-full tablet:h-[18.22vw] relative h-[35.9vw] w-full flex-none">
        <Picture
          source={data.img?.url}
          alt={data.img?.alt || ''}
          className="tablet:block hidden size-full"
          imgClassName="w-full h-full object-cover tablet:hover:scale-110 transition-all duration-300"
        />
        <Picture
          source={data.mobileImg?.url || data.img?.url}
          alt={data.mobileImg?.alt || data.img?.alt || ''}
          className="tablet:hidden block size-full"
          imgClassName="w-full h-full object-cover"
        />
        <div className="laptop:bottom-6 laptop:right-6 absolute bottom-4 right-4">
          {(data?.video?.url || data?.youtubeId) && (
            <button
              onClick={() => {
                configuration?.onVideoPlayBtnClick?.(configuration?.index || 0, data)
              }}
              className="laptop:size-14 bg-opacity/20 flex size-8 items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-black/75"
            >
              <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 7L0 14L0 0L12 7Z" fill="white" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="laptop:gap-[60px] lg-desktop:p-8 tablet:p-6 bg-container-secondary-1 flex min-h-[200px] flex-1 flex-col gap-4 overflow-hidden p-4">
        <div className="flex flex-col">
          <h3 className="lg-desktop:text-[18px] desktop:text-[16px] text-info-primary mb-2 text-[14px] font-bold leading-[1.2] tracking-[-0.02em]">
            {data.title}
          </h3>
          {data.quote && (
            <Link
              asChild={!data?.href}
              href={trackUrlRef(
                getLocalizedPath(data?.href || '', configuration?.locale || 'us'),
                `${componentType}_${componentName}`
              )}
              data-headless-type-name={`${componentType}#${componentName}`}
              data-headless-title-desc-button={`${data?.title}#${data?.description}`}
            >
              <Text
                html={data.quote}
                weight="bold"
                className="lg-desktop:text-[18px] desktop:text-[16px] text-info-primary text-sm"
              />
            </Link>
          )}
        </div>

        <p className="lg-desktop:text-2xl lg-desktop:leading-[1.2] text-info-primary desktop:max-h-[186px] line-clamp-6 max-h-[144px] min-h-[100px] text-xl font-bold leading-[1.2] tracking-[-0.04em]">
          {data.description}
        </p>
      </div>
    </div>
  )
}

const MediaPlayerMulti = forwardRef<HTMLDivElement, MediaPlayerMultiProps>(
  (
    {
      data: { items = [], shape = 'square', title, containerProps },
      className = '',
      key,
      onVideoPlayBtnClick,
      ...rest
    },
    ref
  ) => {
    const [visible, setVisible] = useState<boolean>(false)
    const [videoUrl, setVideoUrl] = useState<string>('')
    const [youTubeId, setYouTubeId] = useState<string>('')
    const { locale = 'us' } = useAiuiContext()
    const title_html = typeof title === 'string' ? title : title && convertLexicalToHTML({ data: title })
    const wrapperRef = useRef<HTMLDivElement>(null)

    // 如果只有两个item, 则平均分配空间
    const itemsLength = items.length === 2

    return (
      <div {...rest} ref={wrapperRef} className={className}>
        <div className="mediaplayermulti-box">
          <Container {...(containerProps || {})} className="overflow-hidden">
            <div className={className} ref={ref}>
              {title && <Title className="mediaplayermulti-title" data={{ title: title_html || '' }} />}
              <SwiperBox
                className="!overflow-visible"
                id={'MediaPlayerMultiSwiper' + key}
                data={{
                  list: items,
                  configuration: {
                    shape,
                    locale,
                    onVideoPlayBtnClick: (_: number, data: MediaPlayerItemProps) => {
                      setVisible(true)
                      if (data?.isYouTube) {
                        setYouTubeId(data?.youtubeId || '')
                        setVideoUrl('')
                      } else {
                        setVideoUrl(data?.video?.url || '')
                        setYouTubeId('')
                      }
                      onVideoPlayBtnClick?.(_)
                    },
                    title: title_html,
                  },
                }}
                Slide={MediaPlayerItem}
                breakpoints={{
                  0: {
                    spaceBetween: 12,
                    freeMode: false,
                    slidesPerView: 1,
                  },
                  374: {
                    spaceBetween: 12,
                    freeMode: false,
                    slidesPerView: 1.2,
                  },
                  768: {
                    spaceBetween: 16,
                    freeMode: false,
                    slidesPerView: itemsLength ? 2 : 2.3,
                  },
                  1024: {
                    spaceBetween: 16,
                    freeMode: false,
                    slidesPerView: itemsLength ? 2 : 1.5,
                  },
                  1440: {
                    spaceBetween: 16,
                    freeMode: false,
                    slidesPerView: 2,
                  },
                }}
              />
            </div>
          </Container>
        </div>
        {visible && (
          <VideoModal
            visible={visible}
            youTubeId={youTubeId}
            videoUrl={videoUrl}
            onCloseModal={() => setVisible(false)}
          />
        )}
      </div>
    )
  }
)

MediaPlayerMulti.displayName = 'MediaPlayerMulti'
export default withLayout(MediaPlayerMulti)
